import type { FC } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import IcBaselineFlag from '~icons/ic/baseline-flag'
import IcBaselineSend from '~icons/ic/baseline-send'
import { RefType, ReportReason } from 'src/services/apollo/generated/graphql'
import { executeReport } from 'src/services/apollo/user'
import { track } from 'src/utils/tracker'
import { UIButton } from '../ui/Button'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
} from '../ui/Modal'

export const AppealDialog: FC<{
  refId: string
  refType: RefType
  description?: string
  onComplete?: () => void
}> = ({ refId, refType, description, onComplete }) => {
  const { t } = useTranslation(['common', 'action'])
  const [submitting, setSubmitting] = useState(false)

  const submit = async () => {
    setSubmitting(true)

    try {
      if (refType !== RefType.Artwork && refType !== RefType.Model) {
        toast.error('Wrong appeal type')
        return
      }
      await executeReport({
        refType,
        refId,
        reason:
          refType === RefType.Artwork
            ? ReportReason.MislabeledArchivedArtwork
            : ReportReason.MislabeledNsfw,
      })
      toast.success(t(`common:appeal.${refType.toLowerCase()}.success`))
      onComplete?.()
    } catch (e) {
      const err = e as Error
      toast.error(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <UIDialogHeader
        heading={
          <span className="inline-flex items-center gap-2">
            <IcBaselineFlag className="size-5" color="inherit" />
            <span>{t(`common:appeal.${refType.toLowerCase()}.title`)}</span>
          </span>
        }
      />
      <UIDialogContent>
        <p className="whitespace-pre-line">
          {description ||
            t(`common:appeal.${refType.toLowerCase()}.description`)}
        </p>
      </UIDialogContent>
      <UIDialogFooter>
        <UIButton
          variant="filled"
          color="admin"
          onPress={submit}
          busy={submitting}
          icon={<IcBaselineSend className="mx-2 size-5" />}
        >
          <span>{t('action:submit')}</span>
        </UIButton>
      </UIDialogFooter>
    </>
  )
}
export const dialogAppeal = async (
  refType: RefType,
  refId: string,
  opts?: { description?: string },
) => {
  track('appeal_click', {
    refType,
    refId,
  })
  return UIDialog.create({
    dismissable: true,
    children: ({ close }) => (
      <AppealDialog
        refType={refType}
        refId={refId}
        description={opts?.description}
        onComplete={() => {
          close()
        }}
      />
    ),
  })
}

export function AppealButtonInDetail(
  props: {
    refType: typeof RefType.Artwork | typeof RefType.Model
    refId: string
    description?: string
  } & StyleProps,
) {
  const { t } = useTranslation(['common'])
  return (
    <UIButton
      variant="filled"
      className={props.className}
      color="danger"
      onPress={() =>
        dialogAppeal(props.refType, props.refId, {
          description: props.description,
        })
      }
    >
      {t('common:appeal.action-label')}
    </UIButton>
  )
}
